import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, Eye, MousePointerClick, Users, TrendingUp, AlertTriangle, Clock, Target } from 'lucide-react';

import { API_URL } from '../../../utils/apiUrl';

const StatCard = ({ icon: Icon, label, value, sub, color = 'bg-black' }) => (
  <div className="bg-white border border-neutral-200 rounded-lg p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-3xl font-black text-black">{typeof value === 'number' ? value.toLocaleString('tr-TR') : value}</p>
    {sub && <p className="text-xs text-neutral-400 mt-1">{sub}</p>}
  </div>
);

const MiniBar = ({ value, max }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
      <div className="h-full bg-black rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
    </div>
  );
};

const DailyChart = ({ data }) => {
  if (!data || data.length === 0) return <p className="text-sm text-neutral-400 py-8 text-center">Henüz veri yok</p>;
  const maxViews = Math.max(...data.map(d => d.views), 1);
  const maxClicks = Math.max(...data.map(d => d.clicks), 1);
  const maxVal = Math.max(maxViews, maxClicks, 1);

  return (
    <div className="flex items-end gap-1 h-40" data-testid="daily-chart">
      {data.map((d, i) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
            {d.date.slice(5)}: {d.views} görüntüleme, {d.clicks} tıklama
          </div>
          <div className="w-full flex gap-px items-end justify-center" style={{ height: '100%' }}>
            <div className="w-1/2 bg-neutral-800 rounded-t-sm transition-all duration-300"
              style={{ height: `${Math.max((d.views / maxVal) * 100, 2)}%` }} />
            <div className="w-1/2 bg-emerald-600 rounded-t-sm transition-all duration-300"
              style={{ height: `${Math.max((d.clicks / maxVal) * 100, 2)}%` }} />
          </div>
          {(i === 0 || i === data.length - 1 || i === Math.floor(data.length / 2)) && (
            <span className="text-[9px] text-neutral-400 mt-1">{d.date.slice(5)}</span>
          )}
        </div>
      ))}
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [days, setDays] = useState(7);
  const [dashboard, setDashboard] = useState(null);
  const [slots, setSlots] = useState([]);
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [premium, setPremium] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, slotRes, prodRes, sellerRes, premRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/dashboard?days=${days}`, { headers }),
        axios.get(`${API_URL}/api/analytics/slots?days=${days}`, { headers }),
        axios.get(`${API_URL}/api/analytics/products?days=${days}`, { headers }),
        axios.get(`${API_URL}/api/analytics/sellers?days=${days}`, { headers }),
        axios.get(`${API_URL}/api/analytics/premium`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setSlots(slotRes.data.slots || []);
      setProducts(prodRes.data.products || []);
      setSellers(sellerRes.data.sellers || []);
      setPremium(premRes.data);
    } catch (e) {
      console.error('Dashboard data error:', e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const dayOptions = [
    { value: 1, label: 'Bugün' },
    { value: 7, label: '7 Gün' },
    { value: 30, label: '30 Gün' },
  ];

  if (loading && !dashboard) {
    return <div className="flex items-center justify-center py-20"><div className="text-neutral-400 font-semibold">Yükleniyor...</div></div>;
  }

  const g = dashboard?.general || {};
  const daily = dashboard?.daily || [];

  return (
    <div data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-black text-black">Dashboard</h2>
          <p className="text-sm text-neutral-400 mt-1">Platform performans verileri</p>
        </div>
        <div className="flex gap-1 bg-neutral-100 rounded-lg p-1" data-testid="date-filter">
          {dayOptions.map(opt => (
            <button key={opt.value} onClick={() => setDays(opt.value)}
              className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${days === opt.value ? 'bg-black text-white' : 'text-neutral-500 hover:text-black'}`}
              data-testid={`filter-${opt.value}d`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* General Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Eye} label="Toplam Ziyaret" value={g.total_events || 0} sub={`Son ${days} gün`} />
        <StatCard icon={Users} label="Tekil Ziyaretçi" value={g.unique_visitors || 0} color="bg-neutral-700" />
        <StatCard icon={BarChart3} label="Ana Sayfa" value={g.homepage_views || 0} color="bg-emerald-700" />
        <StatCard icon={MousePointerClick} label="Ürün Tıklama" value={g.product_clicks || 0} color="bg-amber-700" />
      </div>

      {/* Daily Chart */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-black text-black uppercase tracking-wider">Günlük Trafik</h3>
          <div className="flex items-center gap-4 text-[10px] font-bold">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-neutral-800 rounded-sm" />Görüntüleme</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-600 rounded-sm" />Tıklama</span>
          </div>
        </div>
        <DailyChart data={daily} />
      </div>

      {/* Two Column: Slots + Premium */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Slot Performance */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h3 className="text-sm font-black text-black uppercase tracking-wider mb-4">Slot Performansı</h3>
          {slots.length === 0 ? (
            <p className="text-sm text-neutral-400 py-4">Henüz slot verisi yok</p>
          ) : (
            <div className="space-y-3" data-testid="slot-performance">
              <div className="grid grid-cols-4 text-[10px] font-bold text-neutral-400 uppercase pb-2 border-b">
                <span>Slot</span><span className="text-right">Gösterim</span><span className="text-right">Tıklama</span><span className="text-right">CTR</span>
              </div>
              {slots.map(s => (
                <div key={s.slot} className="grid grid-cols-4 text-sm items-center py-1">
                  <span className="font-black">#{s.slot}</span>
                  <span className="text-right font-semibold text-neutral-600">{s.impressions}</span>
                  <span className="text-right font-semibold text-neutral-600">{s.clicks}</span>
                  <span className="text-right font-bold text-black">{s.ctr?.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Premium Tracking */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h3 className="text-sm font-black text-black uppercase tracking-wider mb-4">Premium Takip</h3>
          {premium && (
            <div data-testid="premium-tracking">
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-black text-emerald-700">{premium.active_slots}</p>
                  <p className="text-[10px] font-bold text-emerald-600 uppercase">Aktif Slot</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-black text-amber-700">{premium.expiring_soon?.length || 0}</p>
                  <p className="text-[10px] font-bold text-amber-600 uppercase">Süresi Dolacak</p>
                </div>
                <div className="text-center p-3 bg-neutral-100 rounded-lg">
                  <p className="text-2xl font-black text-neutral-600">{premium.expired_count}</p>
                  <p className="text-[10px] font-bold text-neutral-500 uppercase">Süresi Dolmuş</p>
                </div>
              </div>
              {premium.expiring_soon?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-amber-600 uppercase mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> 3 Gün İçinde Dolacak
                  </p>
                  {premium.expiring_soon.map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-neutral-100 last:border-0">
                      <span className="font-semibold">Slot #{e.slot} — {e.product_name}</span>
                      <span className="text-xs text-amber-600 font-bold">{e.removal_date?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Product Performance */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-8">
        <h3 className="text-sm font-black text-black uppercase tracking-wider mb-4">Ürün Performansı</h3>
        {products.length === 0 ? (
          <p className="text-sm text-neutral-400 py-4">Henüz ürün verisi yok</p>
        ) : (
          <div data-testid="product-performance">
            <div className="grid grid-cols-6 text-[10px] font-bold text-neutral-400 uppercase pb-2 border-b gap-2">
              <span className="col-span-2">Ürün</span><span className="text-right">Puan</span><span className="text-right">Gösterim</span><span className="text-right">Görüntüleme</span><span className="text-right">Tıklama</span>
            </div>
            {products.map((p, i) => {
              const maxV = Math.max(...products.map(x => x.views), 1);
              return (
                <div key={p.product_id} className="grid grid-cols-6 text-sm items-center py-2.5 border-b border-neutral-50 gap-2">
                  <div className="col-span-2">
                    <p className="font-bold text-black truncate">{p.name}</p>
                    <p className="text-[10px] text-neutral-400">{p.brand}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold text-white ${p.score >= 7 ? 'bg-emerald-700' : 'bg-red-700'}`}>
                      {p.score?.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-right font-semibold text-neutral-600">{p.impressions}</span>
                  <span className="text-right font-semibold text-neutral-600">{p.views}</span>
                  <span className="text-right font-bold text-black">{p.clicks}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Two Column: Sellers + Fallback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seller Performance */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h3 className="text-sm font-black text-black uppercase tracking-wider mb-4">Satıcı Performansı</h3>
          {sellers.length === 0 ? (
            <p className="text-sm text-neutral-400 py-4">Henüz satıcı verisi yok</p>
          ) : (
            <div data-testid="seller-performance">
              {sellers.map((s, i) => {
                const maxSV = Math.max(...sellers.map(x => x.views), 1);
                return (
                  <div key={s.seller_id} className="py-3 border-b border-neutral-100 last:border-0">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <p className="font-bold text-sm text-black">{s.name}</p>
                        {s.email && <p className="text-[10px] text-neutral-400">{s.email}</p>}
                      </div>
                      <div className="text-right text-xs">
                        <span className="font-bold text-black">{s.views}</span>
                        <span className="text-neutral-400 mx-1">görüntüleme</span>
                        <span className="font-bold text-emerald-700">{s.clicks}</span>
                        <span className="text-neutral-400 ml-1">tıklama</span>
                      </div>
                    </div>
                    <MiniBar value={s.views} max={maxSV} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fallback Performance */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h3 className="text-sm font-black text-black uppercase tracking-wider mb-4">Negatif Ürün Performansı</h3>
          <p className="text-[10px] text-neutral-400 mb-3 uppercase">Puan &lt; 7 — Fallback slotlarında gösterilen ürünler</p>
          {premium?.fallback_products?.length > 0 ? (
            <div data-testid="fallback-performance">
              {premium.fallback_products.map((f, i) => {
                const maxFV = Math.max(...premium.fallback_products.map(x => x.views), 1);
                return (
                  <div key={i} className="py-3 border-b border-neutral-100 last:border-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-bold text-sm text-black">{f.name}</p>
                      <div className="text-right text-xs">
                        <span className="font-bold">{f.views}</span>
                        <span className="text-neutral-400 mx-1">/</span>
                        <span className="font-bold text-emerald-700">{f.clicks}</span>
                      </div>
                    </div>
                    <MiniBar value={f.views} max={maxFV} />
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-neutral-400 py-4">Henüz fallback verisi yok</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
